/**
 * Reveal — rises into place when it scrolls into view (web), staggered by
 * `index`. Content is visible by default and only hidden once JS has confirmed
 * it's below the fold and IntersectionObserver is there to bring it back; an
 * element that ends up above the viewport is shown too, so nothing can get
 * stuck invisible (the failure Appear's comment warns about). Content already
 * on screen at mount animates in straight away. Native and reduced
 * motion: renders as a plain View.
 *
 *   <Reveal index={i}>...</Reveal>
 *   <Reveal distance={40} from="scale">...</Reveal>
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import { IS_WEB, useReducedMotion, staggerDelay } from '../../utils/motion';
import injectCss from '../../utils/injectCss';

const CSS = `
[data-reveal="pending"] { opacity: 0; transform: translate3d(0, var(--bo-rv-d, 28px), 0) scale(var(--bo-rv-s, 1)); }
[data-reveal="in"] { opacity: 1; transform: none; transition: opacity 700ms cubic-bezier(0.2,0,0,1), transform 900ms cubic-bezier(0.16,1,0.3,1); transition-delay: var(--bo-rv-delay, 0ms); }
@media (prefers-reduced-motion: reduce) { [data-reveal] { opacity: 1 !important; transform: none !important; transition: none !important; } }
`;

// Layout effect on web so the hidden state lands before first paint (no flash).
const useIsoLayoutEffect = IS_WEB && typeof window !== 'undefined' ? useLayoutEffect : useEffect;

export default function Reveal({ children, index = 0, delay = 0, distance = 28, from = 'rise', style, ...rest }) {
    const ref = useRef(null);
    const reduced = useReducedMotion();
    const [state, setState] = useState(null); // null = plain visible, 'pending', 'in'

    useIsoLayoutEffect(() => {
        if (!IS_WEB || reduced) return undefined;
        const node = ref.current;
        if (!node || typeof window === 'undefined' || !node.getBoundingClientRect) return undefined;
        injectCss('reveal', CSS);
        node.style.setProperty('--bo-rv-d', `${distance}px`);
        node.style.setProperty('--bo-rv-s', from === 'scale' ? '0.96' : '1');
        node.style.setProperty('--bo-rv-delay', `${delay + staggerDelay(index, 70, 420)}ms`);

        // A short timer (not rAF, which stops in background tabs) lets the
        // hidden state paint before the transition to visible starts.
        let timer = 0; let io = null; let pollTimer = 0; let shown = false;
        const show = () => {
            if (shown) return;
            shown = true;
            io?.disconnect();
            window.removeEventListener('scroll', onScroll, true);
            timer = setTimeout(() => setState('in'), 40);
        };
        // Backup to IntersectionObserver (which pauses in hidden tabs and some
        // capture tools): any scroll, in any scroller, re-checks the position.
        function onScroll() {
            if (pollTimer) return;
            pollTimer = setTimeout(() => {
                pollTimer = 0;
                const r = node.getBoundingClientRect();
                if (r.top < window.innerHeight * 0.92 || r.bottom < 0) show();
            }, 90);
        }
        setState('pending');

        const rect = node.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.95 || typeof IntersectionObserver === 'undefined') {
            show();
        } else {
            io = new IntersectionObserver((entries) => {
                // In view, or already scrolled past (fast scroll / jump): show.
                if (entries.some((e) => e.isIntersecting || e.boundingClientRect.bottom < 0)) { io.disconnect(); show(); }
            }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
            io.observe(node);
            window.addEventListener('scroll', onScroll, { capture: true, passive: true });
        }
        return () => { clearTimeout(timer); clearTimeout(pollTimer); io?.disconnect(); window.removeEventListener('scroll', onScroll, true); };
        // Mount-only: one entrance per element.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reduced]);

    return (
        <View ref={ref} style={style} {...(IS_WEB && state ? { dataSet: { reveal: state } } : {})} {...rest}>
            {children}
        </View>
    );
}
